import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, MapPin, Trophy, GraduationCap, ArrowRight } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { UNIVERSITIES } from "../data/universities";
import { COUNTRIES } from "../data/courses";

export default function UniversitiesDirectory() {
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [country, setCountry] = useState("all");

  const filtered = useMemo(() =>
    UNIVERSITIES.filter((u) => {
      const mq = !q || `${u.name} ${u.city} ${u.country}`.toLowerCase().includes(q.toLowerCase());
      const mc = country === "all" || u.country === country;
      return mq && mc;
    }), [q, country]);

  return (
    <div className="min-h-screen bg-[#f6f8fc] flex flex-col">
      <Header />
      <main className="flex-1">
        <section className="bg-[#16244F] py-14 md:py-16">
          <div className="max-w-[1240px] mx-auto px-5 text-center">
            <h1 className="font-head text-3xl md:text-5xl font-bold text-white">University Explorer</h1>
            <p className="text-white/75 mt-4 max-w-2xl mx-auto">Browse top universities worldwide, filter by country, and explore the programmes they offer.</p>
          </div>
        </section>

        <section className="max-w-[1240px] mx-auto px-5 py-10 md:py-14">
          <div className="bg-white rounded-2xl cc-shadow-card p-5 mb-10 grid grid-cols-1 md:grid-cols-[1fr_auto] gap-4">
            <div className="relative">
              <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8a90a0]" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search universities or cities..." className="w-full pl-10 pr-4 py-3 rounded-lg border border-gray-200 focus:border-[#2b8cf0] outline-none transition" />
            </div>
            <select value={country} onChange={(e) => setCountry(e.target.value)} className="px-4 py-3 rounded-lg border border-gray-200 focus:border-[#2b8cf0] outline-none">
              <option value="all">All Countries</option>
              {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>

          {filtered.length === 0 ? (
            <div className="text-center text-[#5a6274] py-16">No universities match your search.</div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-7">
              {filtered.map((u) => (
                <article key={u.id} onClick={() => navigate(`/universities/${u.id}`)} className="cursor-pointer bg-white rounded-2xl cc-shadow-card overflow-hidden group hover:-translate-y-1 transition-transform duration-300 flex flex-col">
                  <div className="relative h-44 overflow-hidden">
                    <img src={u.image} alt={u.name} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" />
                    <div className="absolute bottom-3 left-3 bg-[#16244F]/85 text-white rounded-lg px-2.5 py-1 text-xs font-head inline-flex items-center gap-1"><Trophy size={12} className="text-[#F9B57C]" /> QS #{u.qs}</div>
                  </div>
                  <div className="p-6 flex flex-col flex-1">
                    <h3 className="font-head text-lg font-semibold text-[#16244F] leading-snug group-hover:text-[#F58220] transition-colors">{u.name}</h3>
                    <div className="flex items-center gap-1.5 text-sm text-[#5a6274] mt-1"><MapPin size={14} className="text-[#F58220]" /> {u.city}, {u.country}</div>
                    <p className="text-sm text-[#5a6274] mt-3 line-clamp-2 flex-1">{u.about}</p>
                    <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-100">
                      <span className="inline-flex items-center gap-1.5 text-sm text-[#16244F] font-semibold"><GraduationCap size={16} className="text-[#2b8cf0]" /> {u.programs.length} programmes</span>
                      <span className="inline-flex items-center gap-1 text-[#F58220] font-head font-semibold text-sm group-hover:gap-2 transition-all">Explore <ArrowRight size={15} /></span>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>
      </main>
      <Footer />
    </div>
  );
}
